import { CheckCircleTwoTone, CloseCircleTwoTone, DeleteOutlined, LoadingOutlined, UploadOutlined } from '@ant-design/icons'
import { useApolloClient, useLazyQuery, useMutation } from '@apollo/client'
import { Button, message, notification, Space, Typography, Upload } from 'antd'
import * as PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { useUser } from '../../../../../../context/userContext'
import { uploadFileAndGetSignedUrl, uploadFileToGoogleDrive } from '../../../../../../shared/utils/helpers'
import ImagePreview from '../../../../../common/ImagePreview'

import { StyledUpload } from './patientFiles.styles'
import { SIGN_S3 } from '@dentolize/clinic-mobile/src/shared/store/mutations/authMutations'
import { ADD_NEW_PATIENT_FILE } from '@dentolize/clinic-mobile/src/shared/store/mutations/patientMutations'
import {
  GET_PATIENT_FILES,
  GET_PATIENT_GALLERY,
  PATIENT_FOLDER_DETAILS,
  TOTAL_PATIENT_FILES,
  GET_PATIENT_OPERATIONS
} from '@dentolize/clinic-mobile/src/shared/store/queries/patientQueries'
import GET_GOOGLE_DRIVE_TOKEN from '@dentolize/clinic-mobile/src/shared/store/queries/actions/googleDrive/getGoogleDriveToken'
import { googleDriveFolders } from '@dentolize/clinic-mobile/src/shared/utils/helpers'
import { showAuthError } from '../../../../../../shared/utils/webHelpers'
import { useLoginModal } from '../../../../../../context/loginModalContext'

notification.config({
  maxCount: 1
})

const UploadFileButton = props => {
  const {
    handleAnimateRow,
    forUser = false,
    setImages,
    single,
    images = [],
    folder,
    forGallery,
    small,
    forPayment,
    invoiceId,
    operationId,
    patientId
  } = props

  const [fileList, setFileList] = useState([])
  const [progress, setProgress] = useState(0)

  const { user } = useUser()
  const { t } = useTranslation()
  const client = useApolloClient()
  const { setLoginModalVisible } = useLoginModal()
  const {
    id,
    insuranceCompany,
    supplier,
    lab,
    invoice,
    expense,
    income,
    treasury,
    counter,
    inventoryItem,
    inventoryOrder,
    operation
  } = useParams()

  const [signS3] = useMutation(SIGN_S3)
  const [getDriveToken] = useLazyQuery(GET_GOOGLE_DRIVE_TOKEN)
  const [addNewPatientFile] = useMutation(ADD_NEW_PATIENT_FILE, {
    refetchQueries:
      forGallery && id
        ? folder
          ? [{ query: PATIENT_FOLDER_DETAILS, variables: { folder } }]
          : [{ query: GET_PATIENT_GALLERY, variables: { patient: id } }]
        : []
  })

  useEffect(() => {
    if (fileList.length) {
      notification.open({
        message: t('patients.uploadingFiles'),
        duration: 0,
        description: fileList.map(file => (
          <div key={file.uid}>
            <Typography.Text>
              {file.loading ? (
                <LoadingOutlined className="ant-btn-link" />
              ) : file.loading === false ? (
                <CheckCircleTwoTone twoToneColor="#52c41a" />
              ) : (
                <CloseCircleTwoTone twoToneColor="#ff0000" />
              )}{' '}
              {file.name}
            </Typography.Text>
          </div>
        ))
      })
      if (!fileList.find(file => file.loading)) {
        setFileList([])
        notification.open({
          message: (
            <div>
              {t('patients.completed')}{' '}
              {fileList.find(file => file.loading === null) ? (
                <CloseCircleTwoTone twoToneColor="#ff0000" />
              ) : (
                <CheckCircleTwoTone twoToneColor="#52c41a" />
              )}
            </div>
          ),
          duration: 3,
          description: fileList.map(file => (
            <div key={file.uid}>
              <Typography.Text>
                {file.loading === false ? (
                  <CheckCircleTwoTone twoToneColor="#52c41a" />
                ) : (
                  <CloseCircleTwoTone twoToneColor="#ff0000" />
                )}{' '}
                {file.name}
              </Typography.Text>
            </div>
          ))
        })
      }
    }
  }, [fileList])

  const handleUpload = async file => {
    try {
      const signedUrl = user.company.googleDriveAuth
        ? await uploadFileToGoogleDrive({
            getDriveToken,
            setProgress,
            file,
            client,
            id,
            mainFolder: googleDriveFolders.getDriveName({
              insuranceCompany,
              lab,
              supplier,
              expense,
              inventoryItem,
              inventoryOrder,
              invoice,
              income,
              treasury,
              counter
            })
          })
        : await uploadFileAndGetSignedUrl(signS3, file, null, setProgress)

      const name = file.name.split('.')
      name.pop()

      try {
        const newFile = await addNewPatientFile({
          variables: {
            forUser,
            patient: invoiceId ? undefined : operation || operationId ? patientId || id : id,
            insuranceCompany,
            supplier,
            lab,
            invoice: invoice || invoiceId,
            operation: operation || operationId,
            expense,
            income,
            treasury,
            inventoryItem,
            inventoryOrder,
            counter,
            size: file.size,
            name: name.join('.').substring(0, 99),
            type: file.type.substring(0, 99) || 'file',
            url: signedUrl.data.signS3.url,
            folder
          }
        })

        if (setImages) {
          setImages(images => (single ? [signedUrl.data.signS3.url] : [...images, signedUrl.data.signS3.url]))
        } else if (!forGallery) {
          const queriesToRefetch = [GET_PATIENT_FILES, TOTAL_PATIENT_FILES]
          if (operation || operationId) {
            queriesToRefetch.push(GET_PATIENT_OPERATIONS)
          }
          await client.refetchQueries({ include: queriesToRefetch })
          handleAnimateRow && handleAnimateRow(newFile.data.addNewPatientFile.id, true)
        }
      } catch (e) {
        showAuthError(e, t, setLoginModalVisible)
      }

      setFileList(fileList => [
        ...fileList.filter(oldFile => oldFile.uid !== file.uid),
        {
          uid: file.uid,
          name: file.name,
          status: 'done',
          loading: false,
          url: signedUrl.data.signS3.url,
          thumbUrl: signedUrl.data.signS3.url
        }
      ])
    } catch (e) {
      if (e.message.includes('File too big')) {
        message.error(t('settings.account.fileTooBig'))
      }
      if (e.message.includes('Not enough space')) {
        message.error(t('settings.account.notEnoughSpace'))
      } else {
        showAuthError(e, t, setLoginModalVisible)
      }
      setFileList(fileList => [
        ...fileList.filter(oldFile => oldFile.uid !== file.uid),
        { uid: file.uid, name: file.name, loading: null, status: 'error' }
      ])
    } finally {
      setProgress(0)
    }
  }

  const handleChangeFileList = ({ file }) => {
    setFileList(fileList => [...fileList, { uid: file.uid, name: file.name, loading: true }])
    notification.open({
      message: t('patients.uploadingFiles'),
      description: fileList.map(file => (
        <div key={file.uid}>
          <Typography.Text>
            <LoadingOutlined className="ant-btn-link" /> {file.name}
          </Typography.Text>
        </div>
      ))
    })
  }

  const uploadProps = {
    showUploadList: false,
    multiple: !single,
    onChange: handleChangeFileList,
    maxCount: single ? 1 : null,
    disabled:
      id || forPayment
        ? forUser
          ? !user.permissions.addNewFile
          : !user.permissions.addNewPatientFile
        : !user.permissions.addNewFile,
    beforeUpload: file => {
      const isLt2G = file.size / 1024 / 1024 < 2 * 1024
      if (!isLt2G) {
        message.error(t('patients.maxFileSize', { val: '2 GB' }))
      }
      if (isLt2G) {
        handleUpload(file)
      }
      return false
    }
  }

  return (
    <StyledUpload>
      <Space direction={small ? 'horizontal' : 'vertical'}>
        <Upload {...uploadProps} data-testid="upload-image-container">
          <Button
            disabled={
              id || forPayment
                ? forUser
                  ? !user.permissions.addNewFile
                  : !user.permissions.addNewPatientFile
                : !user.permissions.addNewFile
            }
            size={small ? 'small' : undefined}
            className="hideOnXSmall"
            type={small ? undefined : 'primary'}
            loading={!!fileList.length}
            icon={<UploadOutlined />}
            data-testid="upload-image"
          >
            {t(single ? 'patients.uploadImage' : 'patients.uploadFile')} {progress ? ` ${progress}%` : ''}
          </Button>
        </Upload>
        {images.length ? (
          <div className="images-container flex-container">
            {images.map(src => (
              <div className="image" key={src}>
                <ImagePreview src={src} size={small ? 60 : 100} multiple visibleImage />
                <Button
                  onClick={() => setImages(images => images.filter(i => i !== src))}
                  type="primary"
                  danger
                  size={small ? 'small' : undefined}
                  icon={<DeleteOutlined />}
                  data-testid="delete-image"
                />
              </div>
            ))}
          </div>
        ) : null}
      </Space>
    </StyledUpload>
  )
}

UploadFileButton.propTypes = {
  handleAnimateRow: PropTypes.func,
  setImages: PropTypes.func,
  images: PropTypes.array,
  single: PropTypes.bool,
  small: PropTypes.bool,
  folder: PropTypes.string,
  invoiceId: PropTypes.string,
  forPayment: PropTypes.bool,
  forGallery: PropTypes.bool
}

export default UploadFileButton
